// components/MapScreen/MapComponent.web.js
import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { APIProvider, Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import RestaurantMarker from './RestaurantMarker.web';
import { useSpeedRailzMapPromoUiEnabled } from '../../utils/speedRailzMapUi';
import { useHeavenHillsMapPromoUiEnabled } from '../../utils/heavenHillsMapUi';

// Get Google Maps API key from environment or app.json config
import Constants from 'expo-constants';
const GOOGLE_MAPS_API_KEY =
    process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY ||
    process.env.GOOGLE_MAPS_API_KEY ||
    Constants.expoConfig?.extra?.googleMapsApiKey ||
    Constants.expoConfig?.android?.config?.googleMaps?.apiKey ||
    'AIzaSyAT0z-sNlOjMQ6FvHhba2EI5fbKivAzL18'; // Fallback to key from app.json

function MapContent({ restaurants, selectedRestaurant, onMarkerPress, initialRegion, currentLocation, onRegionChange, userCheckinHistory, mapRef }) {
    const speedRailzPromoEnabled = useSpeedRailzMapPromoUiEnabled();
    const heavenHillsPromoEnabled = useHeavenHillsMapPromoUiEnabled();

    const map = useMap();
    const boundsChangeTimeoutRef = useRef(null);
    const isUserInteractionRef = useRef(false);
    const isMarkerClickRef = useRef(false);

    // Expose map instance via ref (matching mobile app pattern)
    useEffect(() => {
        if (mapRef && map) {
            mapRef.current = map;
        }
    }, [mapRef, map]);

    const handleBoundsChanged = useCallback(() => {
        // Skip bounds updates if it was triggered by a marker click
        if (isMarkerClickRef.current) {
            return; // Don't reset the flag here, let the timeout handle it
        }

        // Debounce bounds changes to avoid excessive updates
        if (boundsChangeTimeoutRef.current) {
            clearTimeout(boundsChangeTimeoutRef.current);
        }

        boundsChangeTimeoutRef.current = setTimeout(() => {
            // Update bounds whenever they change (as long as it's not a marker click)
            // This is a reliable fallback that works even if onDragEnd doesn't fire
            // We check isUserInteractionRef to avoid programmatic updates, but if dragEnd
            // already fired and reset it, we still want to update if bounds changed
            if (!isMarkerClickRef.current && map && onRegionChange) {
                const bounds = map.getBounds();
                if (bounds) {
                    const ne = bounds.getNorthEast();
                    const sw = bounds.getSouthWest();
                    const center = bounds.getCenter();

                    const region = {
                        latitude: center.lat(),
                        longitude: center.lng(),
                        latitudeDelta: Math.abs(ne.lat() - sw.lat()),
                        longitudeDelta: Math.abs(ne.lng() - sw.lng()),
                    };

                    // Update bounds when bounds change (debounced)
                    // This ensures the list refreshes even if onDragEnd doesn't fire
                    onRegionChange(region, false);
                }
            }
        }, 400); // Debounce to avoid excessive updates during dragging
    }, [map, onRegionChange]);

    // Handle map interactions
    const handleMapDragStart = useCallback(() => {
        isUserInteractionRef.current = true;
        isMarkerClickRef.current = false;
    }, []);

    const handleMapZoomChanged = useCallback(() => {
        isUserInteractionRef.current = true;
        isMarkerClickRef.current = false;
        
        // Also update bounds on zoom change to refresh restaurant list
        if (map && onRegionChange) {
            const bounds = map.getBounds();
            if (bounds) {
                const ne = bounds.getNorthEast();
                const sw = bounds.getSouthWest();
                const center = bounds.getCenter();

                const region = {
                    latitude: center.lat(),
                    longitude: center.lng(),
                    latitudeDelta: Math.abs(ne.lat() - sw.lat()),
                    longitudeDelta: Math.abs(ne.lng() - sw.lng()),
                };

                // Update bounds immediately on zoom change
                onRegionChange(region, true);
            }
        }
    }, [map, onRegionChange]);

    // Handle drag end - refresh restaurants list immediately (matching mobile app behavior)
    const handleMapDragEnd = useCallback(() => {
        // Clear any pending debounced updates from handleBoundsChanged
        if (boundsChangeTimeoutRef.current) {
            clearTimeout(boundsChangeTimeoutRef.current);
            boundsChangeTimeoutRef.current = null;
        }

        // Always update bounds when drag ends (unless it was a marker click)
        // Don't check isUserInteractionRef here - we know it's a drag end
        if (!isMarkerClickRef.current && map && onRegionChange) {
            const bounds = map.getBounds();
            if (bounds) {
                const ne = bounds.getNorthEast();
                const sw = bounds.getSouthWest();
                const center = bounds.getCenter();

                const region = {
                    latitude: center.lat(),
                    longitude: center.lng(),
                    latitudeDelta: Math.abs(ne.lat() - sw.lat()),
                    longitudeDelta: Math.abs(ne.lng() - sw.lng()),
                };

                // Pass immediate = true to update bounds immediately on drag end
                onRegionChange(region, true);
            }
        }
        // Reset interaction flag after a short delay to allow handleBoundsChanged fallback
        setTimeout(() => {
            isUserInteractionRef.current = false;
        }, 500);
    }, [map, onRegionChange]);

    // Wrapper for marker press that prevents bounds updates
    const handleMarkerPressWrapper = useCallback((restaurant) => {
        // Set flag to prevent bounds updates for the next 1 second
        isMarkerClickRef.current = true;
        setTimeout(() => {
            isMarkerClickRef.current = false;
        }, 1000);

        // Call the handler immediately
        onMarkerPress(restaurant);
    }, [onMarkerPress]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (boundsChangeTimeoutRef.current) {
                clearTimeout(boundsChangeTimeoutRef.current);
            }
        };
    }, []);

    const defaultCenter = useMemo(() => ({
        lat: initialRegion?.latitude || 30.2672,
        lng: initialRegion?.longitude || -97.7431,
    }), [initialRegion]);

    const zoom = useMemo(() => {
        // Convert latitudeDelta to zoom level (approximate)
        if (initialRegion?.latitudeDelta) {
            const zoomLevel = Math.log2(360 / initialRegion.latitudeDelta);
            return Math.max(10, Math.min(20, zoomLevel));
        }
        return 13;
    }, [initialRegion]);

    const restaurantsOrdered = useMemo(() => {
        if (!restaurants?.length) return restaurants;
        const id = selectedRestaurant?._id;
        if (!id) return restaurants;
        return [...restaurants].sort((a, b) => {
            if (a._id === id) return 1;
            if (b._id === id) return -1;
            return 0;
        });
    }, [restaurants, selectedRestaurant?._id]);

    return (
        <Map
            defaultCenter={defaultCenter}
            defaultZoom={zoom}
            onBoundsChanged={handleBoundsChanged}
            onDragStart={handleMapDragStart}
            onDragEnd={handleMapDragEnd}
            onZoomChanged={handleMapZoomChanged}
            style={styles.map}
            gestureHandling="greedy"
            mapId="c44a0c1bd40e1b3675ae04ec"
            disableDefaultUI={false}
            options={{
                disableDefaultUI: false,
                zoomControl: true,
                mapTypeControl: false,
                scaleControl: false,
                streetViewControl: false,
                rotateControl: false,
                fullscreenControl: false,
            }}
        >
            {useMemo(() => {
                if (!restaurantsOrdered) return null;
                return restaurantsOrdered.map((restaurant) => {
                    if (!restaurant?.latitude || !restaurant?.longitude) return null;

                    const hasCheckedIn = userCheckinHistory?.includes(restaurant._id) || false;
                    const isSelected = selectedRestaurant && selectedRestaurant._id === restaurant._id;

                    return (
                        <RestaurantMarker
                            key={`${restaurant._id}-${isSelected ? '1' : '0'}`}
                            restaurant={restaurant}
                            isSelected={isSelected}
                            onPress={() => handleMarkerPressWrapper(restaurant)}
                            hasCheckedIn={hasCheckedIn}
                            position={{ lat: restaurant.latitude, lng: restaurant.longitude }}
                            speedRailzPromoEnabled={speedRailzPromoEnabled}
                            heavenHillsPromoEnabled={heavenHillsPromoEnabled}
                        />
                    );
                });
            }, [
                restaurantsOrdered,
                selectedRestaurant?._id,
                userCheckinHistory,
                handleMarkerPressWrapper,
                speedRailzPromoEnabled,
                heavenHillsPromoEnabled,
            ])}

            {/* User location marker */}
            {currentLocation && (
                <AdvancedMarker
                    position={{ lat: currentLocation.latitude, lng: currentLocation.longitude }}
                >
                    <div
                        style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: '#4285F4',
                            border: '2px solid #FFFFFF',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        }}
                    />
                </AdvancedMarker>
            )}
        </Map>
    );
}

function MapComponent({ restaurants, selectedRestaurant, onMarkerPress, mapRef, initialRegion, currentLocation, onRegionChange, userCheckinHistory }) {
    if (!GOOGLE_MAPS_API_KEY) {
        return (
            <View style={styles.container}>
                <View style={styles.errorContainer}>
                    <Text style={styles.errorText}>
                        Google Maps API key not configured. Please set EXPO_PUBLIC_GOOGLE_MAPS_API_KEY or GOOGLE_MAPS_API_KEY in your environment variables.
                    </Text>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['marker']}>
                <MapContent
                    restaurants={restaurants}
                    selectedRestaurant={selectedRestaurant}
                    onMarkerPress={onMarkerPress}
                    initialRegion={initialRegion}
                    currentLocation={currentLocation}
                    onRegionChange={onRegionChange}
                    userCheckinHistory={userCheckinHistory}
                    mapRef={mapRef}
                />
            </APIProvider>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        width: '100%',
        height: '100%',
    },
    map: {
        width: '100%',
        height: '100%',
    },
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
        backgroundColor: '#f0f0f0',
    },
    errorText: {
        fontSize: 16,
        color: '#666',
        textAlign: 'center',
    },
});

export default MapComponent;

